// Login and registration hooks with the same shape as Apollo's useMutation
// (`[run, { data, loading, error }]` plus `onCompleted`), so the forms that
// used the GraphQL `login` / `register` mutations only swap the hook.
//
// `data` looks like `{ login: { user } }` / `{ register: { user } }` and
// `error` like `{ graphQLErrors: [{ message }] }`, matching what the forms read.
import { useState } from "react";
import { useApolloClient } from "@apollo/client";

import { GET_USER } from "../DataStructures/Queries";
import { LOGIN } from "./AuthQL";
import {
  loginWithPassword,
  registerAccount,
  errorMessage,
} from "../auth";

const LOGIN_FAILED = "The username and/or password do not match. Please try again.";

// Transition: also set the Django session cookie, because the satellite apps
// (rounds, clinic, cranial, auscultate, kardiograph) still read it when the
// user arrives from medzcool.com. Remove once they all use /auth/exchange/.
async function setSessionCookie(client, username, password) {
  try {
    await client.mutate({ mutation: LOGIN, variables: { username, password } });
  } catch (e) {
    // Tokens already work; the cookie is best effort.
  }
}

async function loadUser(client) {
  const { data } = await client.query({ query: GET_USER, fetchPolicy: "network-only" });
  return data.user;
}

function useAuthAction(key, action, options = {}) {
  const client = useApolloClient();
  const [state, setState] = useState({ data: undefined, loading: false, error: undefined });

  async function run() {
    setState({ data: undefined, loading: true, error: undefined });
    let data;
    try {
      data = await action(client, options.variables || {});
    } catch (e) {
      const error = { graphQLErrors: [{ message: e.message || "Something went wrong." }] };
      setState({ data: undefined, loading: false, error });
      return;
    }
    setState({ data, loading: false, error: undefined });
    if (options.onCompleted) options.onCompleted(data);
    return { data };
  }

  return [run, state];
}

export function useLogin(options) {
  return useAuthAction(
    "login",
    async (client, { username, password }) => {
      const result = await loginWithPassword(username, password);
      if (!result.ok) {
        if (result.status === 401) return { login: { user: null } };
        throw new Error(errorMessage(result, LOGIN_FAILED));
      }
      await setSessionCookie(client, username, password);
      return { login: { user: await loadUser(client) } };
    },
    options
  );
}

export function useRegister(options) {
  return useAuthAction(
    "register",
    async (client, { name, email, password, location }) => {
      const result = await registerAccount({ name, email, password, location });
      if (!result.ok) throw new Error(errorMessage(result, "Could not create the account."));
      await setSessionCookie(client, email, password);
      return { register: { user: await loadUser(client) } };
    },
    options
  );
}
